// Distinct card for `propose_new_node_type` — Tier-2 schema extensions.
// Different visual treatment than GraphProposalCard so the user knows
// the schema (not just an instance) is being extended.
//
// Per PHASE_0_DESIGN.md §7.4: two apply actions, "Add type + create
// instances" and "Add type only", plus a reject path. The agent guarantees
// initial_instances has ≥2 items (server-side validator enforces it).

import { Check, Loader2, ShieldX, Sparkles, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { GraphProposal } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  proposal: GraphProposal;
  /** Apply registers the type. When `withInstances=true`, the server
   * also creates the initial_instances; when false it registers the
   * type only and the agent can propose instances separately later. */
  onApply: (withInstances: boolean) => Promise<void> | void;
  onReject: (userReason?: string) => Promise<void> | void;
  resolved?:
    | { kind: "applied"; affected: number; with_instances: boolean }
    | { kind: "rejected"; reason: string };
}

export function GraphTypeProposalCard({
  proposal,
  onApply,
  onReject,
  resolved,
}: Props) {
  const p = proposal.payload as {
    type_name?: string;
    plural_label?: string;
    description?: string;
    suggested_relationship_from_brand?: string;
    justification?: string;
    initial_instances?: Array<{
      content?: string;
      display_label?: string;
    }>;
  };

  const [busy, setBusy] = useState<"with" | "only" | "reject" | null>(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const instances = p.initial_instances ?? [];

  const handleApply = async (withInstances: boolean) => {
    setBusy(withInstances ? "with" : "only");
    try {
      await onApply(withInstances);
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async () => {
    setBusy("reject");
    try {
      await onReject(rejectReason.trim() || undefined);
    } finally {
      setBusy(null);
      setShowRejectInput(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-lg border-2 border-dashed border-slate-300 bg-card overflow-hidden",
        "shadow-[0_1px_0_rgba(0,0,0,0.02)]"
      )}
    >
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-start gap-3">
        <div className="mt-0.5 h-7 w-7 rounded-md bg-slate-900 text-white flex items-center justify-center">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium tracking-wide uppercase text-slate-700">
            Proposed new node type · schema extension
          </div>
          <div className="text-sm font-semibold text-foreground mt-0.5">
            {p.type_name || "Untitled"}
            {p.plural_label && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({p.plural_label})
              </span>
            )}
          </div>
        </div>
        {resolved && (
          <ResolvedBadge
            kind={resolved.kind}
            withInstances={
              resolved.kind === "applied" ? resolved.with_instances : false
            }
          />
        )}
      </div>

      {p.description && (
        <div className="px-4 pb-2 text-sm text-foreground">
          {p.description}
        </div>
      )}

      <div className="px-4 pb-2 text-xs text-muted-foreground">
        Relationship from Brand:{" "}
        <code className="text-[11px] bg-muted px-1 py-0.5 rounded">
          :{p.suggested_relationship_from_brand}
        </code>
      </div>

      {p.justification && (
        <div className="px-4 pb-3">
          <div className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground mb-1">
            Why a new type
          </div>
          <div className="text-sm text-foreground italic">
            {p.justification}
          </div>
        </div>
      )}

      {/* Initial instances preview */}
      {instances.length > 0 && (
        <div className="px-4 pb-3">
          <div className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground mb-1.5">
            {instances.length} example instance{instances.length === 1 ? "" : "s"}{" "}
            (will be created if you apply with instances)
          </div>
          <ul className="space-y-1">
            {instances.map((inst, i) => (
              <li key={i} className="text-sm text-foreground flex gap-2">
                <span className="text-muted-foreground">●</span>
                {inst.content || (
                  <span className="text-muted-foreground italic">(empty)</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      {!resolved && (
        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={() => handleApply(true)}
            disabled={busy !== null}
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            {busy === "with" ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5 mr-1.5" />
            )}
            Add type + create {instances.length} instance{instances.length === 1 ? "" : "s"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleApply(false)}
            disabled={busy !== null}
          >
            {busy === "only" ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : null}
            Add type only
          </Button>
          {showRejectInput ? (
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Why reject? (optional)"
                className="flex-1 text-sm px-2 py-1 rounded-md border border-border bg-background outline-none focus:ring-2 focus:ring-slate-300"
                autoFocus
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleReject}
                disabled={busy !== null}
              >
                {busy === "reject" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  "Confirm"
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowRejectInput(false);
                  setRejectReason("");
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowRejectInput(true)}
              disabled={busy !== null}
              className="text-muted-foreground"
            >
              <ShieldX className="h-3.5 w-3.5 mr-1.5" />
              Reject
            </Button>
          )}
        </div>
      )}

      {resolved && (
        <div className="px-4 py-2.5 border-t border-border bg-muted/30 text-xs text-muted-foreground">
          {resolved.kind === "applied"
            ? resolved.with_instances
              ? `Type registered + ${resolved.affected - 1} instance${resolved.affected - 1 === 1 ? "" : "s"} created.`
              : "Type registered (no instances)."
            : resolved.reason
              ? `Rejected — "${resolved.reason}".`
              : "Rejected."}
        </div>
      )}
    </div>
  );
}

function ResolvedBadge({
  kind,
  withInstances,
}: {
  kind: "applied" | "rejected";
  withInstances: boolean;
}) {
  if (kind === "applied") {
    return (
      <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        {withInstances ? "Applied + seeded" : "Type registered"}
      </div>
    );
  }
  return (
    <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
      Rejected
    </div>
  );
}
